import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { isEmpty } from "lodash";

import {
  DataGridPro,
  getGridNumericOperators,
  getGridStringOperators,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

import ExportModal from "pages/PipelineReport/components/ExportModal";
import ModalBox from "components/Modal/ModalBox";

import { FilterContext } from "context/FilterContext";
import { UserContext } from "context/UserContext";
import {
  ChartsColor,
  FILTER_TYPES,
  PAGES,
  PitchDeclinedReasonChoices,
  PitchDeclinedReasonText,
  PitchResponseChoices,
  pieChartNameConvention,
} from "helpers/Constant";
import {
  calculateAge,
  convertGridColumnMenu,
  getColumnsAfterChangeOrder,
  orderArrayOfObjectSimilarToRefArrayOfKeys,
  reformatString,
  getCurrentPageAllFiltersFromFilterStore,
  convertStageCodeToName,
  formatDate,
  numberWithCommas
} from "helpers/ReusableFunctions";
import CandidateProfileView from "components/CandidateProfileModalContent/ProfileModalContent";
import CustomFilter from "components/Reports/CustomFilter";
import SaveAsModal from "pages/PipelineReport/components/SaveAsModal";
import { getDetailedApplicationData } from "services/FetchApiData";
import { useTranslation } from "react-i18next";

const ApplicantDetailsTable = ({
  handleSetLocationUsed = () => {},
  locationStateUsed,
  getAllFlagC = false,
  setGetAllFlags,
}) => {
  const { t } = useTranslation();
  const {
    filterStore,
    applyFilterFlags: { applyFilterInEmployeeBranding },
    setLastAppliedFilterstore,
    initialFilterStore,
    setFilterStore,
    Clients,
    setIsDisableApplyButton,
    overrideFilterStoreWithSavedFilters = () => {},
    isPresentationEnabled,
    filterStoreToPassWhenTopFiltersChange,
    isInternalUser,
  } = useContext(FilterContext);

  const { language, userGroup, loggedInUserData } = useContext(UserContext);

  const [tableData, setTableData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [pagination, setPagination] = useState({ limit: 1000, offset: 0 });
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSaveAasModal, setShowSaveAsModal] = useState(false);
  const [initialRenderFlag, setInitialRenderFlag] = useState(true);
  const [isTableLoad, setIsTableLoad] = useState(false);

  const apiRef = useGridApiRef();
  const location = useLocation();
  const state = location.state;

  //? uncomment : if on refresh you don't require custom report view.
  // window.history.replaceState({}, document.title);

  const sortableColumn = true;
  const pinnableColumn = true;

  const handleShowProfileModal = () => {
    setShowProfileModal((current) => !current);
  };

  const handleShowExportModal = () => {
    setShowExportModal((current) => !current);
  };

  const handleShowSaveAsModal = () => {
    setShowSaveAsModal((current) => !current);
  };

  const customStageColumnSorting = (v1, v2, ...rest) => {
    let row1_id = rest[0].id;
    let row2_id = rest[1].id;

    let row1 = rest[0].api.getRow(row1_id);
    let row2 = rest[1].api.getRow(row2_id);

    let intV1 = v1 !== "New Candidate" ? +row1?.[rest[0].field] : 0;
    let intV2 = v2 !== "New Candidate" ? +row2?.[rest[0].field] : 0;
    return intV1 - intV2;
  };

  const filterOperators = getGridStringOperators().filter(
    ({ value }) => !["isEmpty", "isNotEmpty", "isAnyOf"].includes(value)
  );

  const japaneseName = useMemo(
    () => [
      {
        field: "kanji_last",
        headerName: t("performanceReportText.lastNameJ"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "kanji_first",
        headerName: t("performanceReportText.firstNameJ"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const englishName = useMemo(
    () => [
      {
        field: "first_name",
        headerName: t("performanceReportText.firstName"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "last_name",
        headerName: t("performanceReportText.lastName"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: t("performanceReportText.id"),
        width: 30,
      },
      ...(language === "ja"
      ? japaneseName
      : englishName),
      {
        field: "current_employer",
        headerName: t("performanceReportText.employer"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "current_title",
        headerName: t("performanceReportText.title"),
        width: 200,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "estimated_english",
        headerName: t("performanceReportText.estimatedEnglish"),
        width: 200,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "estimated_japanese",
        headerName: t("performanceReportText.estimatedJapanese"),
        width: 200,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "estimated_birth_year",
        headerName: t("performanceReportText.estimatedBirthYear"),
        width: 200,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "estimated_salary",
        headerName: t("performanceReportText.estimatedSalary"),
        width: 200,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        renderCell: ({ value }) => value ? numberWithCommas(value) : "",
      },
      {
        type: "string",
        field: "current_stage",
        headerName: t("performanceReportText.latestStage"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        valueGetter: ({ row }) => {
          return convertStageCodeToName(
            row?.current_stage,
            row?.app_position?.pitch_response.response
          );
        },

        sortComparator: (v1, v2) => {
          const intV1 = customStageColumnSorting;
          const intV2 = customStageColumnSorting;
          return intV1 - intV2;
        },
      },
      {
        field: "last_activity_date",
        headerName: t("performanceReportText.lastActivity"),
        width: 200,
        sortable: sortableColumn,
        pinnableColumn: pinnableColumn,
        filterOperators: filterOperators,
        renderCell: ({ value }) => {
          return formatDate(value);
        },
      },
      ...(!["maybe"].includes(filterStore.PitchResponse)
        ? [
            {
              field: "initial_response",
              headerName: t("performanceReportText.initialResponse"),
              width: 200,
              sortable: sortableColumn,
              pinnable: pinnableColumn,
              filterOperators: filterOperators,
            },
          ]
        : []),
      ...(["no"].includes(filterStore.PitchResponse)
        ? [
            {
              field: "response_reason",
              headerName: t("barChartText.reason"),
              width: 200,
              sortable: sortableColumn,
              pinnable: pinnableColumn,
              filterOperators: filterOperators,
            },
          ]
        : []),
      {
        field: "salary",
        headerName: t("performanceReportText.salary"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        type: "number",
        align: "left",
        headerAlign: "left",
        renderCell: ({ value }) => value ? numberWithCommas(value) : "",
        // valueGetter: (params) => Number(params.value),
      },
      {
        field: "salary_breakdown",
        headerName: t("performanceReportText.salaryBreakdown"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "english",
        headerName: t("performanceReportText.english"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        type: "number",
        align: "left",
        headerAlign: "left",
        // valueGetter: (params) => Number(params.value),
      },
      {
        field: "japanese",
        headerName: t("performanceReportText.japanese"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        type: "number",
        align: "left",
        headerAlign: "left",
        // valueGetter: (params) => {
        //   if (isNaN(params.value)) {
        //     return params.value;
        //   }
        //   return Number(params.value);
        // },
      },
      ...(language === "en" ? japaneseName : englishName),
      {
        field: "linkedin_url",
        headerName: t("performanceReportText.linkedinUrl"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        renderCell: renderSocialLinksActions,
      },
      {
        field: "twitter_url",
        headerName: t("performanceReportText.twitterUrl"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        renderCell: renderSocialLinksActions,
      },
      {
        field: "facebook_url",
        headerName: t("performanceReportText.facebookUrl"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        renderCell: renderSocialLinksActions,
      },
      {
        field: "github_url",
        headerName: t("performanceReportText.githubUrl"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
        renderCell: renderSocialLinksActions,
      },
      {
        field: "age",
        headerName: t("performanceReportText.age"),
        width: 150,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "education_history",
        headerName: t("performanceReportText.educationHistory"),
        width: 200,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
      {
        field: "work_history",
        headerName: t("performanceReportText.workHistory"),
        width: 500,
        sortable: sortableColumn,
        pinnable: pinnableColumn,
        filterOperators: filterOperators,
      },
    ],

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [englishName, filterStore.PitchResponse, japaneseName]
  );

  const initialHiddenColumns = {
    id: false,
  };

  function renderSocialLinksActions(params) {
    return (
      <Link to={params.value} target="_blank" rel="noreferrer">
        {params.value}
      </Link>
    );
  }

  const [columnsOrder, setColumnsOrder] = useState(
    columns?.filter((col) => !initialHiddenColumns[col.field])
  );

  useEffect(() => {
    setColumnsOrder(columns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStore.PitchResponse]);

  const [hiddenColumns, setHiddenColumns] = useState(
    Object.keys(initialHiddenColumns)
  );

  const columnsWithOperators = columnsOrder.map((col) => {
    if (col.type === "number") {
      return {
        ...col,
        filterOperators: [
          ...getGridNumericOperators()
            .filter((operator) => {
              if (operator.value === "=") operator.label = "equals";
              if (operator.value === ">") operator.label = "greater than";
              if (operator.value === "<") operator.label = "less than";
              return (
                operator.value === ">" ||
                operator.value === "<" ||
                operator.value === "="
              );
            })
            .map((operator) => ({
              ...operator,
              InputComponent: CustomFilter,
            })),
        ],
      };
    }

    const customFilterOpeartors = getGridStringOperators().map((operator) => ({
      ...operator,
      InputComponent: CustomFilter,
    }));

    return {
      ...col,
      filterOperators: customFilterOpeartors,
    };
  });

  const getChartName = () => {
    if (filterStore?.PitchResponse) {
      const index = pieChartNameConvention.findIndex(
        (item) => item.value === filterStore?.PitchResponse
      );

      return (
        <span style={{ color: ChartsColor[index] }}>
          {filterStore?.PitchResponse?.charAt(0)?.toUpperCase() +
            filterStore?.PitchResponse?.substring(1)}
        </span>
      );
    }
  };

  const handlePaginationModelChange = ({ pageSize, page }) => {
    let isPageSizeChanged = pagination?.page >= page;
    let newPage = isPageSizeChanged ? 0 : page;
    const newPaging = {
      ...pagination,
      page: newPage,
      limit: pageSize,
      offset: isPageSizeChanged ? 0 : pageSize * page,
    };
    apiRef.current.setPage(newPage);
    setPagination(newPaging);
    fetchData(filterStore, newPaging);
  };

  const fetchData = async (modifiedFilterStore, paginationValues = pagination) => {
    try {
      setIsDisableApplyButton(true);
      setIsTableLoad(true);
      const ApplicationData = await getDetailedApplicationData(
        getCurrentPageAllFiltersFromFilterStore(
          modifiedFilterStore,
          isInternalUser,
          PAGES.EMPLOYEE_BRANDING
        ),
        Clients,
        isPresentationEnabled,
        paginationValues
      );
      setTableData(
        [
        ...(ApplicationData.data.data.reports.applicants.data ?? [])
        ]?.map((user, index) => ({
          id: index + 1,
          age: calculateAge(user.birthday),
          // workhistory: stringSplit(user, 'prospects.workhistory'),
          initial_response: reformatString(
            PitchResponseChoices[user.app_position.pitch_response.response]
          ),
          response_reason:
            PitchDeclinedReasonText(language)?.[
              PitchDeclinedReasonChoices?.[user.app_position?.pitch_response.reason]
            ],
          current_stage: user.app_position.current_stage,
          last_activity_date: new Date(user.last_activity_date),
          ...user,
          estimated_birth_year: calculateAge(user.estimated_birth_year),
          estimated_salary: user?.estimated_salary ? Number(user?.estimated_salary) : null,
          salary: user?.salary ? Number(user?.salary) : null
        }))
      );
      setLastAppliedFilterstore(modifiedFilterStore);
      if (!locationStateUsed) {
        handleSetLocationUsed();
      }
      setPagination({
        ...paginationValues,
        count: ApplicationData?.data.data.reports.applicants.count || 0,
        hasMore: ApplicationData?.data.data.reports.applicants.has_more,
      });
    } catch (error) {
      // Handle error case
      console.log(error);
    } finally {
      setIsTableLoad(false);
      setIsDisableApplyButton(false);
    }
  };

  useEffect(() => {
    let payload = {
      ...filterStore,
      PitchResponse:
        filterStore?.PitchResponse === "candidatesReport"
          ? initialFilterStore?.PitchResponse
          : filterStore?.PitchResponse,
    };
    if (!isEmpty(state?.customReportDetail) && !locationStateUsed) {
      if (
        !isEmpty(state.customReportDetail?.[FILTER_TYPES.LEFT_HAND_FILTERS])
      ) {
        payload = {
          ...overrideFilterStoreWithSavedFilters(filterStore),
        };
      } else {
        payload = {
          ...initialFilterStore,
        };
      }

      if (state?.customReportDetail?.currentGridState) {
        apiRef.current.restoreState(state.customReportDetail.currentGridState);

        //?Below logic bcz : columns orders are not restored by restoreState method
        if (
          state?.customReportDetail?.currentGridState?.columns?.orderedFields
        ) {
          setColumnsOrder((prevColumnsOrder) => {
            const orderedColumns =
              state.customReportDetail.currentGridState.columns.orderedFields;
            const copyPrevColumnsOrder = [...prevColumnsOrder];

            return orderArrayOfObjectSimilarToRefArrayOfKeys(
              copyPrevColumnsOrder,
              orderedColumns,
              "field"
            );
          });
        }
      }
    }
    setFilterStore(payload);
    fetchData(payload);

    return () => setTableData([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterInEmployeeBranding]);

  useEffect(() => {
    const payload = !locationStateUsed
      ? {
          ...overrideFilterStoreWithSavedFilters(
            filterStoreToPassWhenTopFiltersChange
          ),
        }
      : getAllFlagC
      ? {
          ...filterStore,
        }
      : {
          ...filterStoreToPassWhenTopFiltersChange,
        };
    if (getAllFlagC) {
      setGetAllFlags((prev) => ({
        ...prev,
        getAllFlagC: false,
      }));
    }
    if (!initialRenderFlag) {
      fetchData(payload);
    }

    return () => setTableData([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStore.startDate, filterStore.endDate, filterStore.PitchResponse]);

  useEffect(() => {
    if (!initialRenderFlag) {
      setColumnsOrder((prev) => {
        const temp = prev.map((item) =>
          getColumnsAfterChangeOrder(item, columns)
        );

        return temp;
      });
    }
    // eslint-disable-next-line
  }, [language]);

  const pageTitle = useMemo(() => {
    if (!isEmpty(state?.customReportDetail)) {
      return state.customReportDetail.title;
    }
    if (location.pathname === "/performancereport") {
      return t("performanceReportText.headTitle");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, location.pathname]);

  useEffect(() => {
    setInitialRenderFlag(false);
  }, []);

  return (
    <div className="card mt-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title">
            {/* {language !== "English" ? getChartName() : ""}{" "} */}
            {language !== "en"
              ? t(
                  `performanceReportText.applicantDetails.${
                    filterStore?.PitchResponse || "yes"
                  }`
                )
              : ""}
            {language === "en" ? "Detailed Breakdown of" : ""}{" "}
            {language === "en" ? getChartName() : ""}{" "}
            {language === "en"
              ? !isEmpty(state?.customReportDetail)
                ? state.customReportDetail.title
                : "Applicants"
              : ""}
          </h5>

          <div className="d-flex gap-0 flex-shrink-0">
            <div
              className="action-button align-self-center"
              onClick={handleShowSaveAsModal}
            >
              <div className="m-0 flex-shrink-0">
                {t("performanceReportText.saveAs")}
              </div>
            </div>
            {(userGroup.includes("external") ||
              (userGroup.includes("internal") &&
                loggedInUserData.is_admin)) && (
              <div
                className="action-button align-self-center"
                onClick={handleShowExportModal}
              >
                <div className=" m-0">{t("performanceReportText.export")}</div>
              </div>
            )}
          </div>
        </div>
        <div
          className="cstm-mui-datagrid"
          style={{ height: 500, width: "100%" }}
        >
          <DataGridPro
            rows={!isTableLoad ? tableData : []}
            rowCount={pagination?.count}
            pagination
            pageSizeOptions={[50, 100, 500, 1000]}
            onPaginationModelChange={handlePaginationModelChange}
            columns={columnsWithOperators}
            onColumnOrderChange={(c) => {
              setColumnsOrder((prevCols) => {
                const newCols = [...prevCols];
                newCols.splice(c.oldIndex, 1);
                newCols.splice(c.targetIndex, 0, c.column);
                return newCols;
              });
            }}
            slotProps={{
              filterPanel: {
                sx: { maxWidth: "calc(90vw - 24px)" },
              },
              pagination: {
                backIconButtonProps: {
                  disabled: isTableLoad || !pagination?.offset,
                },
                nextIconButtonProps: {
                  disabled: isTableLoad || !pagination?.hasMore,
                },
              },
            }}
            getRowId={(row) => row?.id}
            initialState={{
              ...columns.initialState,
              columns: {
                ...columns.initialState?.columns,
                columnVisibilityModel: initialHiddenColumns,
              },
              pagination: {
                paginationModel: { pageSize: 1000, page: 0 },
              },
            }}
            loading={isTableLoad}
            onRowClick={(params) => {
              setSelectedRow(params.row);
              handleShowProfileModal();
            }}
            onColumnVisibilityModelChange={(hideColumns) => {
              const hideColsName = Object.entries(hideColumns)
                .filter(([key, value]) => value === false)
                ?.map((col) => col[0]);

              // const hideColumnsOrder = [...columnsOrder].filter(
              //   (col) => !hideColsName.includes(col.field)
              // );
              setHiddenColumns(hideColsName);
            }}
            localeText={convertGridColumnMenu(language)}
            rowsLoadingMode={"server"}
            paginationMode={"server"}
            apiRef={apiRef}
          />
        </div>
      </div>
      <ModalBox
        show={showProfileModal}
        onHide={handleShowProfileModal}
        content={
          <CandidateProfileView
            // reportsData={ReportsData}
            closeModal={handleShowProfileModal}
            current={selectedRow}
            title={t("cardViewText.profileView")}
            apiRef={apiRef}
          />
        }
      />
      <ModalBox
        show={showExportModal}
        onHide={handleShowExportModal}
        title="Save As and Export File"
        content={
          <ExportModal
            setShowModal={setShowExportModal}
            columns={columnsOrder}
            fileName={pageTitle}
            apiRef={apiRef}
            hiddenColumns={hiddenColumns}
          />
        }
      />
      <ModalBox
        customClass="special-modal "
        show={showSaveAasModal}
        onHide={handleShowSaveAsModal}
        hideCloseIcon={true}
        content={
          <SaveAsModal
            reportName={pageTitle}
            handleCancel={handleShowSaveAsModal}
            apiRef={apiRef}
            extraTopFiltersToSave={{
              PitchResponse: filterStore.PitchResponse,
              startDate: filterStore.startDate,
              endDate: filterStore.endDate,
            }}
          />
        }
        title={<p className="fs-4 mb-0">Save Custom Report As</p>}
        size="sm"
      />
    </div>
  );
};

export default ApplicantDetailsTable;
